import { useState, useEffect } from 'react';
import { Tag } from 'lucide-react';
import { Button } from '@/components/ui';
import { GalleryListParams, GalleryStatus, ProgramId } from '@/types/gallery';
import { PROGRAM_VALUES, GALLERY_TYPE_OPTIONS } from '../../constants';
import { LocationFilter } from '../../components/LocationFilter';

export interface GalleryFiltersProps {
  filters: GalleryListParams;
  onFiltersChange: (filters: Partial<GalleryListParams>) => void;
}

export const GalleryFilters: React.FC<GalleryFiltersProps> = ({
  filters,
  onFiltersChange
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: string, value: string | number) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value
    }));
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
  };

  const resetFilters = () => {
    const resetFilters = {
      page_number: 1,
      page_size: 10,
      status: 'active' as GalleryStatus
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  // Use centralized program options (ensure includes YOGA)
  const programOptions: ProgramId[] = PROGRAM_VALUES as ProgramId[];

  const statusOptions: GalleryStatus[] = [
    'Active',
    'Inactive'
  ];

  return (
    <div className="bg-white border border-rgf-gray-200 rounded-lg p-6">

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Location Filters */}
          <div>
            <LocationFilter
              value={{
                district: localFilters.district_name,
                subdistrict: localFilters.subdistrict_name,
                village_id: localFilters.village_id,
                village_name: localFilters.village_name
              }}
              onChange={(location) => {
                handleFilterChange('district_name', location.district || '');
                handleFilterChange('subdistrict_name', location.subdistrict || '');
                handleFilterChange('village_id', location.village_id || '');
                handleFilterChange('village_name', location.village_name || '');
              }}
              showVillages={true}
            />
          </div>

          {/* Program & Activity Filters */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-rgf-gray-900 flex items-center">
              <Tag className="h-4 w-4 mr-2" />
              Program & Gallery
            </h4>

            <div>
              <label className="block text-xs font-medium text-rgf-gray-700 mb-1">
                Program
              </label>
              <select
                value={localFilters.program || ''}
                onChange={(e) => handleFilterChange('program', e.target.value)}
                className="w-full px-3 py-2 border border-rgf-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
              >
                <option value="">All Programs</option>
                {programOptions.map(program => (
                  <option key={program} value={program}>{program}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-rgf-gray-700 mb-1">
                Gallery Type
              </label>
              <select
                value={localFilters.type || ''}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-rgf-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
              >
                <option value="">All Types</option>
                {GALLERY_TYPE_OPTIONS.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-rgf-gray-700 mb-1">
                Status
              </label>
              <select
                value={localFilters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-rgf-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
              >
                <option value="">All Statuses</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

        </div>

        {/* Pagination Settings */}
        <div className="border-t pt-6">
          <h4 className="text-sm font-medium text-rgf-gray-900 mb-4">Pagination Settings</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-rgf-gray-700 mb-1">
                Galleries per page
              </label>
              <select
                value={localFilters.page_size || 10}
                onChange={(e) => handleFilterChange('page_size', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-rgf-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-4">
          <Button
            variant="secondary"
            onClick={resetFilters}
          >
            Reset All
          </Button>
          
          <Button
            onClick={applyFilters}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};